import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { SnackBar } from "src/design/SnackBar";
import { InfoCard } from "src/design/InfoCard";
import {
  colorTextPrimary,
  colorTextMuted,
  colorActionDanger,
  colorSurfaceDangerHover,
  colorTextError,
  colorChipBackground,
  colorChipText,
  colorChipBorder,
  radiusChip,
} from "src/design/tokens";
import {
  useRestaurantCapabilities,
  useCreateRestaurantCapability,
  useDeleteRestaurantCapability,
} from "src/data/admin/queries/restaurantCapability";
import { useStaffPermissions } from "src/data/admin/queries/staffPermission";
import { RestaurantCapabilityDto } from "src/data/admin/types/restaurantCapability";
import { CapabilityPermissionsPage } from "./components/CapabilityPermissionsPage";

const Capabilities = () => {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [pendingDelete, setPendingDelete] =
    useState<RestaurantCapabilityDto | null>(null);
  const [selectedCapability, setSelectedCapability] =
    useState<RestaurantCapabilityDto | null>(null);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: capabilities, isLoading } = useRestaurantCapabilities();
  const { data: allPermissions = [] } = useStaffPermissions();

  const createMutation = useCreateRestaurantCapability((c) => {
    setCreateOpen(false);
    setCreateName("");
    setSnack({
      open: true,
      message: `Capability "${c.name}" created.`,
      severity: "success",
    });
  });

  const deleteMutation = useDeleteRestaurantCapability(() => {
    setSnack({
      open: true,
      message: `Capability "${pendingDelete?.name}" deleted.`,
      severity: "success",
    });
    setPendingDelete(null);
  });

  console.log("Capabilities:", capabilities);

  const filtered = (capabilities ?? []).filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleCreate = () => {
    if (!createName.trim()) return;
    createMutation.mutate(
      { name: createName.trim() },
      {
        onError: (err) =>
          setSnack({
            open: true,
            message: err instanceof Error ? err.message : "An error occurred",
            severity: "error",
          }),
      },
    );
  };

  const handleDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onError: (err) =>
        setSnack({
          open: true,
          message: err instanceof Error ? err.message : "An error occurred",
          severity: "error",
        }),
    });
  };

  const handleCloseCreate = () => {
    if (createMutation.isPending) return;
    setCreateOpen(false);
    setCreateName("");
  };

  if (selectedCapability) {
    const liveCapability =
      (capabilities ?? []).find((c) => c.id === selectedCapability.id) ??
      selectedCapability;
    return (
      <CapabilityPermissionsPage
        capability={liveCapability}
        allPermissions={allPermissions}
        onBack={() => setSelectedCapability(null)}
      />
    );
  }

  return (
    <>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: colorTextPrimary }}
          >
            Restaurant Capabilities
          </Typography>
          <CustomButton
            color="black"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New Capability
          </CustomButton>
        </Box>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search capabilities…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colorTextMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3, width: { xs: "100%", md: 320 } }}
        />

        {/* Table */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <InfoCard
            title={`Capabilities (${filtered.length})`}
            icon={<TuneOutlinedIcon fontSize="small" />}
          >
            {filtered.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" sx={{ color: colorTextMuted }}>
                  {search
                    ? "No capabilities match your search."
                    : "No capabilities yet."}
                </Typography>
              </Box>
            ) : (
              <Table size="small" sx={{ mt: -1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ color: colorTextMuted, fontWeight: 600, pl: 0 }}
                    >
                      Name
                    </TableCell>
                    <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                      Restaurants
                    </TableCell>
                    <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                      ID
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((capability) => (
                    <TableRow
                      key={capability.id}
                      onClick={() => setSelectedCapability(capability)}
                      sx={{
                        cursor: "pointer",
                        "&:last-child td": { borderBottom: 0 },
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <TableCell
                        sx={{ pl: 0, fontWeight: 500, color: colorTextPrimary }}
                      >
                        {capability.name}
                      </TableCell>
                      <TableCell>
                        {capability.restaurantIds.length === 0 ? (
                          <Typography
                            variant="body2"
                            sx={{ color: colorTextMuted }}
                          >
                            —
                          </Typography>
                        ) : (
                          <Box
                            sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                          >
                            {capability.restaurantIds.slice(0, 3).map((id) => (
                              <Chip
                                key={id}
                                label={id}
                                size="small"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: 11,
                                  backgroundColor: colorChipBackground,
                                  color: colorChipText,
                                  border: `1px solid ${colorChipBorder}`,
                                  borderRadius: radiusChip,
                                }}
                              />
                            ))}
                            {capability.restaurantIds.length > 3 && (
                              <Chip
                                label={`+${capability.restaurantIds.length - 3}`}
                                size="small"
                                sx={{
                                  fontSize: 11,
                                  backgroundColor: colorChipBackground,
                                  color: colorTextMuted,
                                  border: `1px solid ${colorChipBorder}`,
                                  borderRadius: radiusChip,
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: colorTextMuted,
                          fontFamily: "monospace",
                          fontSize: 12,
                        }}
                      >
                        {capability.id}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ pr: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setPendingDelete(capability)}
                            sx={{
                              color: colorTextMuted,
                              "&:hover": {
                                color: colorActionDanger,
                                backgroundColor: colorSurfaceDangerHover,
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </InfoCard>
        )}
      </Box>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={handleCloseCreate}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>New Capability</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            size="small"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            sx={{ mt: 1 }}
            helperText="Must be unique (e.g. bookings, analytics)"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton color="inverted" onClick={handleCloseCreate}>
            Cancel
          </CustomButton>
          <CustomLoadingButton
            loading={createMutation.isPending}
            disabled={!createName.trim()}
            onSubmit={handleCreate}
            label="Create"
            color="success"
            fullwidth={false}
          />
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!pendingDelete}
        onClose={() => !deleteMutation.isPending && setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Capability</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the capability{" "}
            <strong>{pendingDelete?.name}</strong>? It will be unlinked from all
            associated restaurants.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setPendingDelete(null)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </CustomButton>
          <CustomButton
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{
              backgroundColor: colorActionDanger,
              color: "#fff",
              "&:hover": { backgroundColor: colorTextError },
            }}
          >
            Delete
          </CustomButton>
        </DialogActions>
      </Dialog>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

export default Capabilities;
